/**
 * Отчётность.
 *
 * Решение 5: KPI считаются ТОЛЬКО на сервере. Клиент отображает готовые
 * значения и не пересчитывает их — иначе две реализации формул неизбежно
 * разойдутся, и никто не поймёт, какая права.
 *
 * §17.1: отчётность читает те же нормализованные данные, что и задачник,
 * а не свою копию. Атрибуция проекта и статуса — через общие резолверы
 * (§2.4), поэтому фильтр по проекту в отчёте и на доске даёт один результат.
 */

import type { ReportPeriod } from "@taskflow/contracts";

import { api } from "../api";

export interface ReportScope {
  period: ReportPeriod;
  /** Произвольный период (§17.3): обе границы обязательны при CUSTOM. */
  from?: string;
  to?: string;
  departmentIds?: string[];
  projectIds?: string[];
  boardIds?: string[];
  /** Детализация по сотруднику: задачи, где он исполнитель. */
  assigneeIds?: string[];
}

export interface OverviewKpi {
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  /** Среднее время выполнения в днях; null — за период ничего не выполнено. */
  avgCycleDays: number | null;
  deltaTotalPercent: number | null;
  deltaCompletedPercent: number | null;
  deltaOverduePercent: number | null;
  deltaCyclePercent: number | null;
}

export interface Overview {
  kpi: OverviewKpi;
  statusBreakdown: { id: string; label: string; value: number }[];
  trend: { label: string; value: number }[];
  /** Шаг столбцов динамики: день — у недели и месяца, неделя — у длинных периодов. */
  trendStep: "day" | "week";
  /** Периоду может не соответствовать ни одна задача — §17.3 требует
   *  отличать «нет данных» от «ошибка». */
  isEmptyPeriod: boolean;
  /** Границы периода, ISO — для подписей и окна экспорта. */
  period: { from: string; to: string };
  /** Дата среза: на неё считается просрочка. */
  referenceDate: string;
  /** Предыдущий отрезок той же длины и его значения — база процентов. */
  comparison: {
    from: string;
    to: string;
    totalTasks: number;
    completedTasks: number;
    overdueTasks: number;
    avgCycleDays: number | null;
  };
}

export interface RankingEntry {
  id: string;
  name: string;
  total: number;
  completed: number;
  overdue: number;
  /** Доля завершённых, %. Считает сервер. */
  completionRate: number;
  avgCycleDays: number | null;
  /** Только в рейтинге сотрудников: незавершённые задачи периода. */
  inProgress?: number;
  /** Только у сотрудников: задачи с несколькими исполнителями — каждая
   *  входит в строку каждого из них. */
  shared?: number;
  /** Только у сотрудников: основное подразделение. */
  departmentId?: string | null;
  departmentName?: string | null;
}

/** Итог таблицы сотрудников — по уникальным задачам, а не сумма строк. */
export interface EmployeesTotals {
  total: number;
  completed: number;
  overdue: number;
  completionRate: number;
  inProgress: number;
  shared: number;
}

export interface EmployeesReport {
  data: RankingEntry[];
  totals: EmployeesTotals;
  /** Задачи периода без исполнителя: в «Всего задач» входят, в строки — нет. */
  unassigned: number;
  /** Варианты фильтра «Отдел» — из всей таблицы, до отбора. */
  departments: { id: string; name: string }[];
  attention: AttentionItem[];
  workload: WorkloadEntry[];
  workloadRules: WorkloadRules;
}

/** Отбор строк таблицы «Сотрудники»: по отделу сотрудника и ФИО. */
export interface EmployeesFilter {
  employeeDepartmentIds?: string[];
  employeeSearch?: string;
}

export interface AttentionItem {
  key: string;
  title: string;
  boardId: string;
  boardName: string;
  departmentName: string;
  reason: "OVERDUE" | "NO_ASSIGNEE" | "STALE" | "BLOCKED";
  reasonLabel: string;
  daysOverdue: number | null;
  assigneeNames: string[];
}

export interface WorkloadEntry {
  userId: string;
  name: string;
  departmentName: string | null;
  /** Открытые задачи на дату среза. */
  activeTasks: number;
  overdueTasks: number;
  /** Баллы: Σ вес приоритета × множитель срока. */
  points: number;
  /** Норма отдела в баллах; null — не задана и посчитать не из чего. */
  norm: number | null;
  /** DEPARTMENT — задана отделу; DEPARTMENT_AVERAGE — среднее по отделу. */
  normSource: "DEPARTMENT" | "DEPARTMENT_AVERAGE" | null;
  /** Загрузка относительно нормы, %. Больше 100 — перегрузка; null — нормы нет. */
  loadPercent: number | null;
}

/** Правила баллов нагрузки — приходят с сервера, подсказка их не выдумывает. */
export interface WorkloadRules {
  priorities: { name: string; weight: number }[];
  noPriorityWeight: number;
  overdueFactor: number;
  dueSoonFactor: number;
  dueSoonDays: number;
}

/**
 * Плитка «Обзора», от которой открыт список задач (§17.2).
 * Отбор делает сервер той же формулой, что считала число на плитке.
 */
export type TaskKpi = "completed" | "overdue";

/** Строка таблицы задач отчёта (§17.6) — и детализации по сущности. */
export interface ReportTaskRow {
  key: string;
  title: string;
  boardId: string;
  boardName: string;
  /** Подразделение доски задачи. */
  departmentName: string;
  projectName: string | null;
  statusLabel: string;
  /** Статус задачи (категория столбца или системный тег); null — не задан. */
  statusName: string | null;
  assigneeNames: string[];
  dueDate: string | null;
  overdue: boolean;
  createdAtLabel: string;
  startedAtLabel: string | null;
  completedAtLabel: string | null;
  /** Итог по сроку: «В срок», «С опозданием на N дн.», «Просрочено на N дн.». */
  resultLabel: string;
  /** Те же моменты в ISO 8601 — для сортировки. */
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  /**
   * Срок жизни задачи: от создания до завершения, у открытой — до сегодня.
   * Рабочие — пн–пт. Считает сервер (решение 5).
   */
  workDays: number;
  calendarDays: number;
}

/** Что выгружать: раздел и его сужения или весь отчёт. */
export interface ExportRequest {
  view: string;
  scope: ReportScope;
  /** FULL — весь отчёт; SECTION — открытый раздел с его сужениями. */
  extent: "FULL" | "SECTION";
  /** Провал с плитки «Обзора» — тот же отбор, что у списка задач. */
  kpi?: TaskKpi;
  /** Номера задач в порядке таблицы на экране (её фильтры и сортировка). */
  keys?: string[];
  /** Условия таблицы словами — в шапку файла. */
  filtersLabel?: string;
}

/** Шапка будущего файла — собирает сервер той же функцией, что и файл. */
export interface ExportPreview {
  title: string;
  periodLabel: string;
  referenceLabel: string;
  filtersLabel: string;
  authorLabel: string;
  /** Состав файла: «Сводка», таблицы разделов, «Список задач». */
  sections: string[];
  tasksCount: number;
}

export const reportsApi = api.injectEndpoints({
  endpoints: (build) => ({
    overview: build.query<Overview, ReportScope>({
      query: (params) => ({ method: "reports.overview", params }),
      providesTags: ["Report"],
    }),

    employeesReport: build.query<EmployeesReport, ReportScope & EmployeesFilter>({
      query: (params) => ({ method: "reports.employees", params }),
      providesTags: ["Report"],
    }),

    departmentsReport: build.query<{ data: RankingEntry[] }, ReportScope>({
      query: (params) => ({ method: "reports.departments", params }),
      providesTags: ["Report"],
    }),

    projectsReport: build.query<{ data: RankingEntry[] }, ReportScope>({
      query: (params) => ({ method: "reports.projects", params }),
      providesTags: ["Report"],
    }),

    reportTasks: build.query<
      { data: ReportTaskRow[] },
      ReportScope & { search?: string; kpi?: TaskKpi }
    >({
      query: (params) => ({ method: "reports.tasks", params }),
      providesTags: ["Report"],
    }),

    savedReports: build.query<
      { data: { id: string; name: string; view: string; createdAtLabel: string }[] },
      void
    >({
      query: () => ({ method: "savedReports.list" }),
      providesTags: ["SavedReport"],
    }),

    saveReport: build.mutation<
      { id: string },
      { name: string; view: string; scope: ReportScope; clientRequestId: string }
    >({
      // §17.8: сохраняется представление — фильтры и период, а не копия данных.
      query: (params) => ({ method: "savedReports.create", params }),
      invalidatesTags: ["SavedReport"],
    }),

    createExport: build.mutation<
      { jobId: string },
      ExportRequest & { format: "PDF" | "XLSX" | "DOCX"; clientRequestId: string }
    >({
      // Сервер ждёт область отчёта на верхнем уровне, как reports.tasks.
      query: ({ scope, ...rest }) => ({ method: "reports.export.create", params: { ...scope, ...rest } }),
    }),

    // Та же шапка, что будет в файле: окно экспорта не собирает её само.
    exportPreview: build.query<
      ExportPreview,
      ExportRequest
    >({
      query: ({ scope, ...rest }) => ({
        method: "reports.export.preview",
        params: { ...scope, ...rest },
      }),
      providesTags: ["Report"],
    }),

    // Мутация, а не запрос: ссылка подписана на минуты и кэшироваться не должна.
    exportStatus: build.mutation<
      | { jobId: string; status: "PENDING" }
      | { jobId: string; status: "READY"; format: string; sizeBytes: number; downloadUrl: string },
      { jobId: string }
    >({
      query: (params) => ({ method: "reports.export.status", params }),
    }),
  }),
});

export const {
  useExportStatusMutation,
  useExportPreviewQuery,
  useOverviewQuery,
  useEmployeesReportQuery,
  useDepartmentsReportQuery,
  useProjectsReportQuery,
  useReportTasksQuery,
  useSavedReportsQuery,
  useSaveReportMutation,
  useCreateExportMutation,
} = reportsApi;
